import {
  powerBubbleChartData,
  type PowerLabData,
  type PowerRegionData,
} from "./powerBubbleChartData"

export type RegionLayout = {
  region: PowerRegionData
  x: number
  y: number
  width: number
  height: number
}

export type PackedLab = {
  lab: PowerLabData
  x: number
  y: number
  size: number
}

export const REGION_GAP = 6
export const LAB_GAP = 4
export const REGION_PADDING = 10
export const REGION_HEADER_HEIGHT = 36
export const CHART_MAX_WIDTH = 455
const CHART_WIDTH_FILL = 1
const CHART_LOG_AREA_SCALE = 6_600
const CHART_HEIGHT_SCALE = 1.15
const CHART_SCALE_SEARCH_STEPS = 24
const LOG_COMPUTE_UNIT = 20_000
const LAB_AREA_SCALE = 0.08
const LAB_MIN_SIZE = 12
const LAB_MAX_SIZE = 28

// Matches the HUD's shared transition window (HUD_SYNC_TRANSITION_MS).
export const BUBBLE_TRANSITION = [
  "left 500ms ease",
  "top 500ms ease",
  "width 500ms ease",
  "height 500ms ease",
].join(", ")

const clamp = (value: number, min: number, max: number) => (
  Math.min(max, Math.max(min, value))
)
const getLogScaledCompute = (compute: number) => Math.log10(1 + compute / LOG_COMPUTE_UNIT)
const getAreaForUnitScale = (compute: number, areaPerScaledComputeUnit: number) => (
  getLogScaledCompute(compute) * areaPerScaledComputeUnit
)

export const getChartDimensions = (regionLayouts: RegionLayout[]) => ({
  width: regionLayouts.length === 0
    ? 0
    : Math.max(...regionLayouts.map(layout => layout.x + layout.width)),
  height: regionLayouts.length === 0
    ? 0
    : Math.max(...regionLayouts.map(layout => layout.y + layout.height)),
})

const getPackedLabsHeight = (packedLabs: PackedLab[]) => (
  packedLabs.length === 0
    ? 0
    : Math.max(...packedLabs.map(packedLab => packedLab.y + packedLab.size))
)

export const getLabSize = (compute: number, areaPerScaledComputeUnit: number) => (
  clamp(
    Math.sqrt(getAreaForUnitScale(compute, areaPerScaledComputeUnit) * LAB_AREA_SCALE),
    LAB_MIN_SIZE,
    LAB_MAX_SIZE
  )
)

const getRegionMinimumWidth = (
  region: PowerRegionData,
  areaPerScaledComputeUnit: number
) => {
  const labSizes = region.labs.map(lab => getLabSize(lab.compute, areaPerScaledComputeUnit))
  const largestLabSize = Math.max(LAB_MIN_SIZE, ...labSizes)
  return REGION_PADDING * 2 + largestLabSize
}

type LabBox = { lab: PowerLabData, size: number }
type PackingCursor = { x: number, y: number, rowHeight: number }

const getLabBoxesSortedBySize = (
  labs: PowerLabData[],
  areaPerScaledComputeUnit: number
): LabBox[] => (
  [...labs]
    .map(lab => ({ lab, size: getLabSize(lab.compute, areaPerScaledComputeUnit) }))
    .sort((a, b) => b.size - a.size)
)

const advancePackingCursorWithNextLab = (
  cursor: PackingCursor,
  labBox: LabBox,
  rowWidth: number
) => {
  const shouldStartNewRow = cursor.x > 0 && cursor.x + labBox.size > rowWidth
  const x = shouldStartNewRow ? 0 : cursor.x
  const y = shouldStartNewRow ? cursor.y + cursor.rowHeight + LAB_GAP : cursor.y
  const rowHeight = shouldStartNewRow ? labBox.size : Math.max(cursor.rowHeight, labBox.size)
  return {
    packedLab: { lab: labBox.lab, x, y, size: labBox.size } satisfies PackedLab,
    nextCursor: { x: x + labBox.size + LAB_GAP, y, rowHeight } satisfies PackingCursor,
  }
}

export const packLabs = (
  labs: PowerLabData[],
  width: number,
  areaPerScaledComputeUnit: number
) => {
  const sortedLabBoxes = getLabBoxesSortedBySize(labs, areaPerScaledComputeUnit)
  const packedLabs: PackedLab[] = []
  let cursor: PackingCursor = { x: 0, y: 0, rowHeight: 0 }
  for (const labBox of sortedLabBoxes) {
    const result = advancePackingCursorWithNextLab(cursor, labBox, width)
    packedLabs.push(result.packedLab)
    cursor = result.nextCursor
  }
  return packedLabs
}

const getLayoutWithMinimumLabHeight = (
  layout: RegionLayout,
  areaPerScaledComputeUnit: number
) => {
  const labAreaWidth = Math.max(0, layout.width - REGION_PADDING * 2)
  const packedLabs = packLabs(layout.region.labs, labAreaWidth, areaPerScaledComputeUnit)
  const minimumHeight = REGION_HEADER_HEIGHT + REGION_PADDING + getPackedLabsHeight(packedLabs)
  return { ...layout, height: Math.max(layout.height, minimumHeight) }
}

type RegionTriple = {
  america: PowerRegionData
  china: PowerRegionData
  world: PowerRegionData
}

type ColumnGeometry = {
  americaWidth: number
  americaHeight: number
  chinaWidth: number
  chinaHeight: number
  worldWidth: number
  worldHeight: number
}

const getRegionTriple = (regions: PowerRegionData[]): RegionTriple | null => {
  const america = regions.find(region => region.id === "america")
  const china = regions.find(region => region.id === "china")
  const world = regions.find(region => region.id === "world")
  if (!america || !china || !world) return null
  return { america, china, world }
}

const getRegionSquareWidth = (
  region: PowerRegionData,
  areaPerScaledComputeUnit: number
) => {
  const area = getAreaForUnitScale(region.computeStock, areaPerScaledComputeUnit)
  const side = Math.sqrt(area)
  return Math.max(side, getRegionMinimumWidth(region, areaPerScaledComputeUnit))
}

const getRegionSquareHeight = (
  region: PowerRegionData,
  areaPerScaledComputeUnit: number
) => {
  const area = getAreaForUnitScale(region.computeStock, areaPerScaledComputeUnit)
  return Math.sqrt(area) * CHART_HEIGHT_SCALE
}

const getColumnGeometry = (
  triple: RegionTriple,
  areaPerScaledComputeUnit: number
): ColumnGeometry => ({
  americaWidth: getRegionSquareWidth(triple.america, areaPerScaledComputeUnit),
  americaHeight: getRegionSquareHeight(triple.america, areaPerScaledComputeUnit),
  chinaWidth: getRegionSquareWidth(triple.china, areaPerScaledComputeUnit),
  chinaHeight: getRegionSquareHeight(triple.china, areaPerScaledComputeUnit),
  worldWidth: getRegionSquareWidth(triple.world, areaPerScaledComputeUnit),
  worldHeight: getRegionSquareHeight(triple.world, areaPerScaledComputeUnit),
})

const buildLayoutsFromGeometry = (
  triple: RegionTriple,
  geometry: ColumnGeometry,
  areaPerScaledComputeUnit: number
): RegionLayout[] => {
  const americaLayout = getLayoutWithMinimumLabHeight(
    {
      region: triple.america,
      x: 0,
      y: 0,
      width: geometry.americaWidth,
      height: geometry.americaHeight,
    },
    areaPerScaledComputeUnit
  )
  const chinaLayout = getLayoutWithMinimumLabHeight(
    {
      region: triple.china,
      x: geometry.americaWidth + REGION_GAP,
      y: 0,
      width: geometry.chinaWidth,
      height: geometry.chinaHeight,
    },
    areaPerScaledComputeUnit
  )
  const worldLayout = getLayoutWithMinimumLabHeight(
    {
      region: triple.world,
      x: geometry.americaWidth + REGION_GAP + geometry.chinaWidth + REGION_GAP,
      y: 0,
      width: geometry.worldWidth,
      height: geometry.worldHeight,
    },
    areaPerScaledComputeUnit
  )
  return [americaLayout, chinaLayout, worldLayout]
}

export const getRegionLayoutsForAreaPerScaledComputeUnit = (
  regions: PowerRegionData[],
  areaPerScaledComputeUnit: number
) => {
  const triple = getRegionTriple(regions)
  if (!triple) return []
  const geometry = getColumnGeometry(triple, areaPerScaledComputeUnit)
  return buildLayoutsFromGeometry(triple, geometry, areaPerScaledComputeUnit)
}

const getMaxChartWidthForScale = (areaPerScaledComputeUnit: number) => (
  Math.max(...powerBubbleChartData.map(entry => (
    getChartDimensions(
      getRegionLayoutsForAreaPerScaledComputeUnit(entry.regions, areaPerScaledComputeUnit)
    ).width
  )))
)

const refineFittingScaleBounds = (
  bounds: { min: number, max: number },
  chartWidthTarget: number
) => {
  const candidate = (bounds.min + bounds.max) / 2
  if (getMaxChartWidthForScale(candidate) <= chartWidthTarget) {
    return { min: candidate, max: bounds.max }
  }
  return { min: bounds.min, max: candidate }
}

const binarySearchForLargestFittingScale = (chartWidthTarget: number) => {
  let bounds = { min: 0, max: CHART_LOG_AREA_SCALE }
  const scaleSearchSteps = Array.from({ length: CHART_SCALE_SEARCH_STEPS })
  for (const _step of scaleSearchSteps) {
    bounds = refineFittingScaleBounds(bounds, chartWidthTarget)
  }
  return bounds.min
}

export const getCalibratedAreaPerScaledComputeUnit = (chartWidthLimit: number) => {
  const chartWidthTarget = chartWidthLimit * CHART_WIDTH_FILL
  if (getMaxChartWidthForScale(CHART_LOG_AREA_SCALE) <= chartWidthTarget) {
    return CHART_LOG_AREA_SCALE
  }
  return binarySearchForLargestFittingScale(chartWidthTarget)
}

// Used by the simpler equal-column variant where countries are NOT sized by compute;
// only the labs use this scale to determine their square sizes.
export const EQUAL_COLUMN_AREA_PER_SCALED_COMPUTE_UNIT = CHART_LOG_AREA_SCALE

// Single-row equal-column variant: derive an absolute pixel width per lab using a
// SHARED global compute^exponent scale across all regions. The binding constraint
// is whichever region's labs would otherwise overflow its row first, ensuring every
// region fits while also making the largest lab (across the whole world) the widest
// bubble overall. Smaller regions naturally leave trailing space at the row's end.
//
// computeWidthExponent tunes how dramatically width tracks compute:
//   0   -> all labs same width
//   0.5 -> sqrt (moderate; widths differ by ~6x top-to-bottom)
//   1   -> linear share (most dramatic; tiny labs become very narrow)
export const getCrossRegionLabWidthsByLabId = (
  regions: PowerRegionData[],
  regionWidth: number,
  labGap: number,
  minLabWidth: number,
  computeWidthExponent: number
): Map<string, number> => {
  const getLabWeight = (compute: number) => Math.pow(compute, computeWidthExponent)
  const scaleCandidatesPerRegion = regions.map(region => {
    const labWeightSum = region.labs.reduce(
      (sum, lab) => sum + getLabWeight(lab.compute),
      0
    )
    const numGaps = Math.max(0, region.labs.length - 1)
    const availableLabRowWidth = Math.max(0, regionWidth - numGaps * labGap)
    return labWeightSum > 0 ? availableLabRowWidth / labWeightSum : Infinity
  })
  const globalScaleFactor = scaleCandidatesPerRegion.length === 0
    ? 0
    : Math.min(...scaleCandidatesPerRegion)
  const labWidthByLabId = new Map<string, number>()
  for (const region of regions) {
    for (const lab of region.labs) {
      const rawWidth = globalScaleFactor * getLabWeight(lab.compute)
      labWidthByLabId.set(lab.id, Math.max(minLabWidth, rawWidth))
    }
  }
  return labWidthByLabId
}

// Top N labs by compute within a region. Used by the equal-column variant to
// decide which labs render their icon vs. fall back to dimmed black squares.
export const getTopLabIdsByCompute = (
  labs: PowerLabData[],
  topN: number
): Set<string> => (
  new Set(
    [...labs]
      .sort((a, b) => b.compute - a.compute)
      .slice(0, Math.max(0, topN))
      .map(lab => lab.id)
  )
)

export const getEqualColumnRegionLayouts = (
  regions: PowerRegionData[],
  totalWidth: number,
  areaPerScaledComputeUnit: number
): RegionLayout[] => {
  const triple = getRegionTriple(regions)
  if (!triple) return []
  const columnWidth = Math.max(0, (totalWidth - REGION_GAP * 2) / 3)
  const orderedRegions = [triple.america, triple.china, triple.world]
  return orderedRegions.map((region, index) => (
    getLayoutWithMinimumLabHeight(
      {
        region,
        x: index * (columnWidth + REGION_GAP),
        y: 0,
        width: columnWidth,
        height: 0,
      },
      areaPerScaledComputeUnit
    )
  ))
}
